import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { QRCode } from 'react-qr-code'
import { ChevronDown, Download, FileText, Archive, Crosshair, Eye, Box, QrCode as QrCodeIcon, X } from 'lucide-react'
import { downloadQRPdfGrid, downloadQRZip, serializeSvgFromDom } from '../lib/printQR'

const C = {
  display: { fontFamily: "'VT323', monospace", letterSpacing: '0.08em', textTransform: 'uppercase' },
  mono: { fontFamily: "'JetBrains Mono', monospace" },
  panel: { background: 'rgba(0, 20, 8, .4)', border: '1px solid rgba(0,255,102,.18)', boxShadow: '0 0 6px rgba(0,255,102,.08)', position: 'relative' },
  btnPrimary: { fontFamily: "'VT323', monospace", fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase', background: '#00ff66', color: '#000', border: 'none', padding: '6px 14px', cursor: 'pointer', boxShadow: '0 0 6px #00ff66', display: 'inline-flex', alignItems: 'center', gap: 6 },
  btnSecondary: { fontFamily: "'JetBrains Mono', monospace", fontSize: 11, background: 'rgba(0, 20, 8, .4)', color: '#4aaa5a', border: '1px solid rgba(0,255,102,.18)', padding: '6px 14px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 },
  input: { background: '#0a140a', color: '#00ff66', border: '1px solid rgba(0,255,102,.3)', padding: '4px 10px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, outline: 'none' },
}

// One section row — checkbox + label. Controlled by `selected` set on parent.
function SelectableRow({ id, primary, secondary, color = '#9aff9a', selected, onToggle }) {
  return (
    <label className="flex items-center gap-3 px-2 py-1 cursor-pointer hover:bg-black/10">
      <input type="checkbox" checked={selected} onChange={() => onToggle(id)} />
      <div className="flex-1 min-w-0">
        <p style={{ ...C.mono, fontSize: 12, color: '#e0e8de' }} className="truncate">{primary}</p>
        {secondary && <p style={{ ...C.mono, fontSize: 10, color }} className="truncate">{secondary}</p>}
      </div>
    </label>
  )
}

// Section wrapper — collapsible header + body. Persists collapsed state.
function Section({ id, icon: Icon, title, count, children, color = '#00ff66' }) {
  const lsKey = `dc_qr_dlc_${id}_collapsed`
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(lsKey) === 'true')
  const toggle = () => { const n = !collapsed; setCollapsed(n); localStorage.setItem(lsKey, String(n)) }
  return (
    <div className="mb-4" style={C.panel}>
      <button onClick={toggle} className="w-full flex items-center justify-between px-3 py-2"
        style={{ background: 'rgba(0,255,102,.04)', border: 'none', cursor: 'pointer', color }}>
        <span className="flex items-center gap-2" style={{ ...C.display, fontSize: 14 }}>
          {Icon && <Icon size={14} />} {title} <span style={{ ...C.mono, fontSize: 11, opacity: 0.7 }}>({count})</span>
        </span>
        <ChevronDown size={14} style={{ transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform .2s' }} />
      </button>
      {!collapsed && <div className="p-3">{children}</div>}
    </div>
  )
}

export default function QRDownloadCenter({ event, onClose }) {
  const [players, setPlayers] = useState([])
  const [factions, setFactions] = useState([])
  const [triggers, setTriggers] = useState([])
  const [equipment, setEquipment] = useState([])
  const [loading, setLoading] = useState(true)
  // Per-section selection sets keyed by id.
  const [selPlayers, setSelPlayers] = useState(new Set())
  const [selTriggers, setSelTriggers] = useState(new Set())
  const [selEquipment, setSelEquipment] = useState(new Set())
  const [joinCount, setJoinCount] = useState(8)
  const [exporting, setExporting] = useState(false)

  useEffect(() => { fetchAll() }, [event.id])
  const fetchAll = async () => {
    setLoading(true)
    const [{ data: p }, { data: f }, { data: t }, { data: e }] = await Promise.all([
      supabase.from('event_players')
        .select('user_id, faction_id, users(callsign, display_name, email)')
        .eq('event_id', event.id),
      supabase.from('event_factions').select('id, name, color').eq('event_id', event.id),
      // Only triggers with a real (scannable) trigger_qr — the
      // attached-to-player intel uses placeholder QRs and shouldn't be
      // exported here.
      supabase.from('intel_triggers')
        .select('id, message, commander_message, trigger_qr, attached_to_player_id')
        .eq('event_id', event.id),
      supabase.from('event_equipment')
        .select('id, name, description, equipment_qr, status')
        .eq('event_id', event.id),
    ])
    setPlayers(p || [])
    setFactions(f || [])
    setTriggers((t || []).filter(x => !x.attached_to_player_id))
    setEquipment(e || [])
    setLoading(false)
  }

  const playerName = (p) => p.users?.callsign || p.users?.display_name || p.users?.email || 'Unknown'
  const safe = (s) => String(s || '').replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, '-').toLowerCase()

  const toggle = (set, setter) => (id) => {
    const next = new Set(set)
    if (next.has(id)) next.delete(id); else next.add(id)
    setter(next)
  }
  const setAll = (setter, ids) => setter(new Set(ids))
  const setSelectByFaction = (factionId) => {
    const ids = players.filter(p => p.faction_id === factionId).map(p => p.user_id)
    setSelPlayers(new Set(ids))
  }

  // Walk a list of items, capture the rendered <svg> for each from the
  // hidden export DOM, and hand it off to the downloader. We render every
  // selectable QR into a hidden grid (out of the document flow) so any
  // selection is exportable without re-rendering on click.
  const exportFor = async (kind, fileName, mode /* 'pdf' | 'zip' */) => {
    setExporting(true)
    try {
      let items = []
      if (kind === 'players') {
        items = players
          .filter(p => selPlayers.has(p.user_id))
          .map(p => ({
            svgString: serializeSvgFromDom(`bulkqr-player-${p.user_id}`),
            label: playerName(p),
            fileName: `player-${safe(playerName(p))}`,
          }))
      } else if (kind === 'triggers') {
        items = triggers
          .filter(t => selTriggers.has(t.id))
          .map((t, idx) => ({
            svgString: serializeSvgFromDom(`bulkqr-trigger-${t.id}`),
            label: `INTEL-${String(idx + 1).padStart(2, '0')} ${t.message?.slice(0, 24) || ''}`.trim(),
            fileName: `intel-${String(idx + 1).padStart(2, '0')}-${safe(t.message?.slice(0, 24) || t.id.slice(0, 8))}`,
          }))
      } else if (kind === 'equipment') {
        items = equipment
          .filter(eq => selEquipment.has(eq.id))
          .map(eq => ({
            svgString: serializeSvgFromDom(`bulkqr-equip-${eq.id}`),
            label: eq.name,
            fileName: `equip-${safe(eq.name)}`,
          }))
      } else if (kind === 'join') {
        const svg = serializeSvgFromDom('bulkqr-join')
        const n = Math.max(1, Math.min(50, parseInt(joinCount) || 1))
        items = Array.from({ length: n }, (_, i) => ({
          svgString: svg,
          label: `${event.name} · SCAN TO JOIN`,
          fileName: `join-${safe(event.join_code)}-${String(i + 1).padStart(2, '0')}`,
        }))
      }

      items = items.filter(x => x.svgString)
      if (items.length === 0) { alert('Nothing selected.'); return }
      if (mode === 'pdf') await downloadQRPdfGrid(items, { fileName, watermark: `${event.name?.toUpperCase() || 'DEATH CARD'} · ANUBIS` })
      else await downloadQRZip(items, { fileName })
    } catch (err) {
      console.error('[QRDownloadCenter] export failed', err)
      alert('Export failed: ' + (err?.message || 'unknown'))
    } finally {
      setExporting(false)
    }
  }

  const eventJoinUrl = `https://death-card.vercel.app/join/${event.join_code}`

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,.85)' }}>
      <div className="w-full max-w-3xl my-6 p-4" style={{ ...C.panel, background: '#0a140a' }}>
        <div className="flex items-center justify-between mb-4">
          <h2 style={{ ...C.display, fontSize: 18, color: '#00ff66' }}>
            <QrCodeIcon size={16} style={{ display: 'inline', marginRight: 8, verticalAlign: 'middle' }} />
            BULK QR DOWNLOAD
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#4aaa5a', cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <p style={{ ...C.mono, fontSize: 12, color: '#4aaa5a' }}>Loading event QR data...</p>
        ) : (
          <>
            {/* Player QRs */}
            <Section id="players" icon={Crosshair} title="Player QRs" count={players.length} color="#00ff66">
              <div className="flex gap-2 mb-3 flex-wrap">
                <button onClick={() => setAll(setSelPlayers, players.map(p => p.user_id))} style={C.btnSecondary}>Select All</button>
                <button onClick={() => setSelPlayers(new Set())} style={C.btnSecondary}>Clear</button>
                {factions.map(f => (
                  <button key={f.id} onClick={() => setSelectByFaction(f.id)} style={{ ...C.btnSecondary, color: f.color, borderColor: f.color + '66' }}>
                    Select {f.name}
                  </button>
                ))}
              </div>
              <div className="max-h-56 overflow-y-auto" style={{ border: '1px solid rgba(0,255,102,.1)' }}>
                {players.length === 0 ? <p className="p-2 text-xs" style={{ color: '#2a6a3a' }}>No players</p> : players.map(p => (
                  <SelectableRow key={p.user_id} id={p.user_id}
                    primary={playerName(p)}
                    secondary={factions.find(f => f.id === p.faction_id)?.name || 'Unassigned'}
                    color={factions.find(f => f.id === p.faction_id)?.color || '#4aaa5a'}
                    selected={selPlayers.has(p.user_id)}
                    onToggle={toggle(selPlayers, setSelPlayers)} />
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => exportFor('players', `${safe(event.name)}-players`, 'pdf')} disabled={exporting || selPlayers.size === 0} style={{ ...C.btnPrimary, opacity: (exporting || selPlayers.size === 0) ? 0.5 : 1 }}>
                  <FileText size={12} /> PDF ({selPlayers.size})
                </button>
                <button onClick={() => exportFor('players', `${safe(event.name)}-players`, 'zip')} disabled={exporting || selPlayers.size === 0} style={{ ...C.btnSecondary, opacity: (exporting || selPlayers.size === 0) ? 0.5 : 1 }}>
                  <Archive size={12} /> ZIP ({selPlayers.size})
                </button>
              </div>
            </Section>

            {/* Intel Trigger QRs */}
            <Section id="triggers" icon={Eye} title="Intel Trigger QRs" count={triggers.length} color="#ffcc33">
              <div className="flex gap-2 mb-3 flex-wrap">
                <button onClick={() => setAll(setSelTriggers, triggers.map(t => t.id))} style={C.btnSecondary}>Select All</button>
                <button onClick={() => setSelTriggers(new Set())} style={C.btnSecondary}>Clear</button>
              </div>
              <div className="max-h-56 overflow-y-auto" style={{ border: '1px solid rgba(255,204,51,.15)' }}>
                {triggers.length === 0 ? <p className="p-2 text-xs" style={{ color: '#2a6a3a' }}>No intel triggers</p> : triggers.map((t, i) => (
                  <SelectableRow key={t.id} id={t.id}
                    primary={`INTEL-${String(i + 1).padStart(2, '0')} · ${t.message?.slice(0, 40) || '(no player message)'}`}
                    secondary={t.commander_message?.slice(0, 50) || null}
                    color="#ffcc33"
                    selected={selTriggers.has(t.id)}
                    onToggle={toggle(selTriggers, setSelTriggers)} />
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => exportFor('triggers', `${safe(event.name)}-intel`, 'pdf')} disabled={exporting || selTriggers.size === 0} style={{ ...C.btnPrimary, opacity: (exporting || selTriggers.size === 0) ? 0.5 : 1 }}>
                  <FileText size={12} /> PDF ({selTriggers.size})
                </button>
                <button onClick={() => exportFor('triggers', `${safe(event.name)}-intel`, 'zip')} disabled={exporting || selTriggers.size === 0} style={{ ...C.btnSecondary, opacity: (exporting || selTriggers.size === 0) ? 0.5 : 1 }}>
                  <Archive size={12} /> ZIP ({selTriggers.size})
                </button>
              </div>
            </Section>

            {/* Equipment QRs */}
            <Section id="equipment" icon={Box} title="Equipment QRs" count={equipment.length} color="#9aff9a">
              <div className="flex gap-2 mb-3 flex-wrap">
                <button onClick={() => setAll(setSelEquipment, equipment.map(e => e.id))} style={C.btnSecondary}>Select All</button>
                <button onClick={() => setSelEquipment(new Set())} style={C.btnSecondary}>Clear</button>
              </div>
              <div className="max-h-56 overflow-y-auto" style={{ border: '1px solid rgba(0,255,102,.1)' }}>
                {equipment.length === 0 ? <p className="p-2 text-xs" style={{ color: '#2a6a3a' }}>No equipment</p> : equipment.map(eq => (
                  <SelectableRow key={eq.id} id={eq.id}
                    primary={eq.name}
                    secondary={eq.description?.slice(0, 50) || null}
                    color="#9aff9a"
                    selected={selEquipment.has(eq.id)}
                    onToggle={toggle(selEquipment, setSelEquipment)} />
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => exportFor('equipment', `${safe(event.name)}-equipment`, 'pdf')} disabled={exporting || selEquipment.size === 0} style={{ ...C.btnPrimary, opacity: (exporting || selEquipment.size === 0) ? 0.5 : 1 }}>
                  <FileText size={12} /> PDF ({selEquipment.size})
                </button>
                <button onClick={() => exportFor('equipment', `${safe(event.name)}-equipment`, 'zip')} disabled={exporting || selEquipment.size === 0} style={{ ...C.btnSecondary, opacity: (exporting || selEquipment.size === 0) ? 0.5 : 1 }}>
                  <Archive size={12} /> ZIP ({selEquipment.size})
                </button>
              </div>
            </Section>

            {/* Join Code QR — single QR, repeated N times */}
            <Section id="join" icon={QrCodeIcon} title="Join Code QR" count={1} color="#4da0ff">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <span style={{ ...C.mono, fontSize: 12, color: '#9aa6b0' }}>Copies:</span>
                <input type="number" min={1} max={50} value={joinCount}
                  onChange={e => setJoinCount(e.target.value)} style={{ ...C.input, width: 80 }} />
                <span style={{ ...C.mono, fontSize: 11, color: '#4aaa5a' }}>{event.join_code}</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => exportFor('join', `${safe(event.name)}-join`, 'pdf')} disabled={exporting} style={{ ...C.btnPrimary, opacity: exporting ? 0.5 : 1 }}>
                  <FileText size={12} /> PDF Sheet
                </button>
                <button onClick={() => exportFor('join', `${safe(event.name)}-join`, 'zip')} disabled={exporting} style={{ ...C.btnSecondary, opacity: exporting ? 0.5 : 1 }}>
                  <Archive size={12} /> ZIP
                </button>
              </div>
            </Section>
          </>
        )}

        {exporting && (
          <p className="text-center mt-4" style={{ ...C.mono, fontSize: 11, color: '#ffcc33' }}>
            Generating export...
          </p>
        )}

        {/* Hidden render area — every QR lives here at all times so any
            selection is exportable without a re-render dance. Kept off-screen
            (not display:none, since serializing a display:none SVG can fail
            in some browsers). */}
        <div style={{ position: 'fixed', left: -99999, top: 0, width: 256, height: 256 }} aria-hidden="true">
          {players.map(p => (
            <div key={`p-${p.user_id}`} style={{ background: '#fff' }}>
              <QRCode id={`bulkqr-player-${p.user_id}`} value={`deathcard://claim/${p.user_id}`} size={256} />
            </div>
          ))}
          {triggers.map(t => (
            <div key={`t-${t.id}`} style={{ background: '#fff' }}>
              <QRCode id={`bulkqr-trigger-${t.id}`} value={t.trigger_qr} size={256} />
            </div>
          ))}
          {equipment.map(eq => (
            <div key={`e-${eq.id}`} style={{ background: '#fff' }}>
              <QRCode id={`bulkqr-equip-${eq.id}`} value={eq.equipment_qr} size={256} />
            </div>
          ))}
          <div style={{ background: '#fff' }}>
            <QRCode id="bulkqr-join" value={eventJoinUrl} size={256} />
          </div>
        </div>
      </div>
    </div>
  )
}
